import {
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  Renderer2,
} from '@angular/core';
import { DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS } from '@shared';
import { Mappers } from './file-upload-v2.helpers';
import { FileUploadV2Store } from './file-upload-v2.store';

@Directive({
  selector: '[squadboxFileUploadTrigger]',
})
export class FileUploadTriggerDirective {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  private readonly fileInput: HTMLInputElement;

  /**
   * Sets or retrieves the Boolean value indicating whether multiple items can be selected from a list.
   *
   * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/multiple)
   */
  public readonly multiple = input<boolean>(false);
  /**
   * Emits the state of the file upload.
   */
  public readonly store = input.required<FileUploadV2Store>();
  /**
   * Sets or retrieves a comma-separated list of content types. An input so the
   * skill builder can pass its image-free / md-only zone lists; defaults to
   * today's computed chat list, so chat binds nothing and stays identical.
   *
   * [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLInputElement/accept)
   */
  public readonly accept = input<string>(
    DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS.map((type) => `.${type}`).join(','),
  );

  constructor() {
    // Create file input dynamically
    this.fileInput = this.renderer.createElement('input') as HTMLInputElement;
    this.renderer.setAttribute(this.fileInput, 'type', 'file');
    this.renderer.setAttribute(this.fileInput, 'hidden', 'true');
    // Append to host element
    this.renderer.appendChild(this.el.nativeElement, this.fileInput);
    // Effect to keep the file input options in sync with the inputs. `accept`
    // is read inside the effect (not once in the constructor) so a bound
    // `[accept]` lands — a constructor read of a signal input yields the default
    // before bindings resolve, silently ignoring the builder's list.
    effect(() => {
      this.renderer.setAttribute(this.fileInput, 'accept', this.accept());
    });
    effect(() => {
      this.renderer.setProperty(this.fileInput, 'multiple', this.multiple());
    });
    // Add file change listener
    this.fileInput.addEventListener('change', this.onFilesChange.bind(this));
    // NB: no auto-upload effect here — uploading is driven by the store's
    // `addFiles` (see FileUploadV2Store). Driving it from a directive effect
    // aborts in-flight POSTs on directive teardown (STT destroys this directive
    // mid-upload) and re-runs on the sync pending→ongoing flip.
  }

  /**
   * Clicking on the host element will trigger the file input.
   */
  @HostListener('click')
  onClick() {
    this.fileInput.value = ''; // This make sure we can re-upload the same file
    this.fileInput.click();
  }

  /**
   * When the file input changes, we add the files to the store.
   */
  private onFilesChange(event: Event) {
    const files = (event.target as HTMLInputElement).files;

    if (!files) {
      return;
    }

    this.store().addFiles(
      Array.from(files).map((file) => Mappers.toFileUploadStateFile(file)),
    );
  }
}
